/**
 * Profit and loss — ROADMAP 6.4.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ONE CONVENTION, STATED ONCE: REALISED P&L IS GROSS
 *
 *     realised  =  gross, BEFORE any charge
 *     net       =  realised − chargesTotal
 *
 * Every writer stores GROSS. Nothing stores net; net is derived, here, by the
 * one function below. That is not a style preference — QbarTrade shipped
 * exactly one writer that stored net, and because every reader then
 * subtracted charges again, it double-subtracted fees on every broker trade,
 * silently, for months. The number looked plausible the whole time, which is
 * why nobody caught it.
 *
 * A test asserts the convention rather than trusting the comment.
 *
 * SIGN: positive is profit, for both directions. A short that is bought back
 * cheaper is a profit and reads positive, the same as a long sold higher.
 * ─────────────────────────────────────────────────────────────────────────────
 */
/**
 * GROSS realised P&L for a closed quantity.
 *
 * The sign of `qty` does all the work: a long is `(exit − entry) × qty` with
 * qty positive, and a short is the same expression with qty negative, which
 * flips it. One formula, no branch, so there is no second path to get the
 * short case backwards in.
 */
export function realisedPnl(input) {
    const { qty, entryPrice, exitPrice } = input;
    if (!Number.isFinite(qty) || !Number.isFinite(entryPrice) || !Number.isFinite(exitPrice)) {
        return 0;
    }
    return round2((exitPrice - entryPrice) * qty);
}
/**
 * Unrealised P&L on an open position, marked at `ltp`.
 *
 * Returns null rather than 0 when there is no mark. Zero is a real P&L —
 * a position exactly at its entry — and a position whose price is unknown is
 * not that. Collapsing the two is how an unmarked leg quietly contributes
 * nothing to a total that claims to describe it.
 */
export function unrealisedPnl(input) {
    const { qty, entryPrice, ltp } = input;
    if (ltp === null || !Number.isFinite(ltp) || ltp <= 0)
        return null;
    if (!Number.isFinite(qty) || !Number.isFinite(entryPrice))
        return null;
    return round2((ltp - entryPrice) * qty);
}
/**
 * THE ONLY PLACE CHARGES ARE SUBTRACTED.
 *
 * If a second one ever appears, the two will disagree and the difference
 * will be exactly one set of fees — which is the failure this module's
 * header describes.
 */
export function netPnl(grossPnl, chargesTotal) {
    const gross = Number.isFinite(grossPnl) ? grossPnl : 0;
    const charges = Number.isFinite(chargesTotal) ? Math.max(0, chargesTotal) : 0;
    return round2(gross - charges);
}
/** Rupees to the paisa. */
const round2 = (v) => Math.round(v * 100) / 100;
export const sideOf = (qty) => (qty >= 0 ? 'LONG' : 'SHORT');
//# sourceMappingURL=pnl.js.map